export const LEGAL_PAGE_TABS = [
  { key: 'documents', label: 'Documents' },
  { key: 'privacy_policy', label: 'Privacy Policy' },
  { key: 'cookies', label: 'Cookies' },
  { key: 'risk_warning', label: 'Risk Warning' },
];

export const LEGAL_DOCUMENT_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'revoked', label: 'Revoked' },
];

function normalizeText(value) {
  return value === null || value === undefined ? '' : String(value).trim();
}

function formatDateLabel(value) {
  const normalized = normalizeText(value);
  if (!normalized) {
    return '-';
  }

  return normalized.includes('T') ? normalized.slice(0, 10) : normalized;
}

function getStatusWeight(status) {
  const normalized = normalizeText(status).toLowerCase();
  if (normalized === 'active') {
    return 0;
  }
  if (normalized === 'draft') {
    return 1;
  }
  if (normalized === 'revoked') {
    return 2;
  }
  return 3;
}

export function getLegalDocumentStatusBadgeClass(status) {
  const normalized = normalizeText(status).toLowerCase();
  if (normalized === 'active') {
    return 'badge bg-success-subtle text-success';
  }
  if (normalized === 'draft') {
    return 'badge bg-warning-subtle text-warning';
  }
  if (normalized === 'revoked') {
    return 'badge bg-danger-subtle text-danger';
  }
  return 'badge bg-secondary-subtle text-secondary';
}

export function buildLegalDocumentRowsModel(dtoRows) {
  return [...(Array.isArray(dtoRows) ? dtoRows : [])]
    .map((item, index) => {
      const uid = normalizeText(item?.uid);
      const status = normalizeText(item?.status);
      const effectiveDate = normalizeText(item?.effectiveDate);
      const expirationDate = normalizeText(item?.expirationDate);

      return {
        key: uid || `legal-document-${index}`,
        uid,
        name: normalizeText(item?.name),
        version: normalizeText(item?.version),
        status,
        statusLabel: status || '-',
        statusBadgeClass: getLegalDocumentStatusBadgeClass(status),
        effectiveDate,
        effectiveDateLabel: formatDateLabel(effectiveDate),
        expirationDate,
        expirationDateLabel: formatDateLabel(expirationDate),
        downloadUrl: normalizeText(item?.downloadUrl),
        canDownload: Boolean(uid),
      };
    })
    .sort((a, b) => {
      const byStatus = getStatusWeight(a.status) - getStatusWeight(b.status);
      if (byStatus !== 0) {
        return byStatus;
      }

      return b.effectiveDate.localeCompare(a.effectiveDate);
    });
}

export function countActiveLegalDocuments(rows) {
  return (Array.isArray(rows) ? rows : []).filter((row) => normalizeText(row?.status).toLowerCase() === 'active').length;
}

export function buildLegalDocumentUploadForm() {
  return {
    name: '',
    version: '',
    status: 'draft',
    effectiveDate: '',
    expirationDate: '',
    document: null,
  };
}

export function validateLegalDocumentUploadForm(form) {
  const errors = {};

  ['name', 'version', 'status'].forEach((key) => {
    if (!normalizeText(form?.[key])) {
      errors[key] = 'This field is required.';
    }
  });

  if (!form?.document) {
    errors.document = 'Choose a document file.';
  }

  return errors;
}

export function buildEmptyLegalContentForm(key) {
  return {
    key: normalizeText(key),
    title: '',
    version: '',
    effectiveDate: '',
    html: '',
  };
}

export function buildLegalContentFormModel(dto) {
  return {
    key: normalizeText(dto?.key),
    title: normalizeText(dto?.title),
    version: normalizeText(dto?.version),
    effectiveDate: formatDateLabel(dto?.effectiveDate) === '-' ? '' : formatDateLabel(dto?.effectiveDate),
    html: normalizeText(dto?.html),
  };
}

export function normalizeLegalHtmlForSave(sourceHtml) {
  const html = String(sourceHtml || '');
  return html
    .replace(/&nbsp;/gi, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/\{\{([\s\S]*?)\}\}/g, (_match, inner) => `{{${String(inner || '').replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' ')}}}`);
}

export function buildSandboxPreviewHtml(sourceHtml) {
  return `<!doctype html><html><head><base target="_blank"><style>body{font-family:Arial,sans-serif;font-size:14px;line-height:1.5;color:#1f2937;margin:0;}a{color:#0d6efd;}</style></head><body>${String(sourceHtml || '')}</body></html>`;
}
